import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, cn } from '../../ui';
import { stackOf, type MemberRow } from '../members/rules';

/**
 * The people on the board in the top bar (backlog §19 P16-3): up to three others, people here now first, then "+N";
 * alone, my own avatar. One button that opens the members panel (following is done from there).
 */
export function AvatarStack({
  rows,
  me,
  following,
  open,
  onToggle,
  docked = false,
  buttonRef,
}: {
  rows: readonly MemberRow[];
  me: { name: string; color: string };
  following: string | null;
  open: boolean;
  onToggle: () => void;
  docked?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  const { t } = useTranslation();
  const { shown, more } = stackOf(rows);
  const online = rows.filter((r) => r.active && !r.you).length;
  const faces = shown.length
    ? shown.map((r) => ({ id: r.userId, name: r.name, color: r.color, photo: r.photo, elsewhere: r.elsewhere }))
    : [{ id: 'me', name: me.name, color: me.color, photo: rows.find((r) => r.you)?.photo, elsewhere: undefined }];
  return (
    <Button
      ref={buttonRef}
      variant="ghost"
      aria-label={t('members.open', { count: online })}
      aria-expanded={open}
      onClick={onToggle}
      className={cn('gap-1 rounded-full px-1 aria-expanded:bg-surface-2', docked ? 'h-9' : 'absolute right-3 top-3 z-20 bg-surface shadow-md')}
    >
      <span className="flex -space-x-2">
        {faces.map((f) => (
          <span
            key={f.id}
            data-avatar=""
            data-followed={following === f.id || undefined}
            data-elsewhere={f.elsewhere ? '' : undefined}
            title={f.elsewhere ? `${f.name} — ${f.elsewhere}` : undefined}
            className={cn('rounded-full', following === f.id && 'ring-2 ring-focus', f.elsewhere && 'opacity-60')}
          >
            <Avatar name={f.name} color={f.color} photo={f.photo} size="md" />
          </span>
        ))}
      </span>
      {more > 0 && <span className="px-1 font-mono text-xs text-fg-muted">{t('members.more', { count: more })}</span>}
    </Button>
  );
}
